import _ChevronRight from "@mui/icons-material/ChevronRight";
import _Chip from "@mui/material/Chip";
import _Divider from "@mui/material/Divider";
import _Box from "@mui/material/Box";
import _Skeleton from "@mui/material/Skeleton";
import _ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import _ToggleButton from "@mui/material/ToggleButton";
import _Typography from "@mui/material/Typography";
import _Button2 from "@mui/material/Button";
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useChallenge } from '../../../hooks';
import useChallengeLeaderboardQuery from '../../../hooks/use-challenge-leaderboard-query';
import useEventRankingQuery from '../../../hooks/use-event-ranking-query';
import { GeogroupAvatar } from '../../geogroup-avatar';
import { UserAvatar } from '../../user';
import { medals } from '../medals';
/**
 * Merges the national (companies) and internal (own company's cyclists) rankings in a single
 * card with a segmented control to switch between the two.
 */
function RankingCard({
  Button: _Button,
  challenge,
  currentUser,
  event,
  onFocusOwnCompanyInLeaderboard,
  ownGeogroup,
  selectLeaderboardScope,
  selectTab
}) {
  const [tab, setTab] = useState('national');
  const {
    t
  } = useTranslation();
  const {
    getProgressValueWithUnit
  } = useChallenge();
  const {
    data: nationalTopResult
  } = useEventRankingQuery(event.id, {
    page: 1,
    pageSize: 3,
    groupTypes: ['company']
  });
  const nationalTop = nationalTopResult?.items;
  const {
    data: nationalOwnResult
  } = useEventRankingQuery(ownGeogroup ? event.id : undefined, {
    page: 1,
    pageSize: 1,
    groupTypes: ['company'],
    groupsIds: ownGeogroup ? [ownGeogroup.id] : undefined
  });
  const nationalOwnItem = ownGeogroup === undefined ? undefined : ownGeogroup === null ? null : nationalOwnResult ? nationalOwnResult.items[0] ?? null : undefined;
  const {
    data: internalTopResult
  } = useChallengeLeaderboardQuery(challenge?.id, {
    page: 1,
    pageSize: 3,
    addEmployeeData: false
  });
  const internalTop = internalTopResult?.leaderboard;
  const {
    data: internalOwnResult
  } = useChallengeLeaderboardQuery(challenge && currentUser ? challenge.id : undefined, {
    page: 1,
    pageSize: 1,
    userId: currentUser?.id,
    addEmployeeData: false
  });
  // Waiting for the user to be resolved, otherwise the own-rank row would be fetched as anonymous
  // and stay empty for a logged-in user.
  const internalOwnItem = currentUser === undefined ? undefined : currentUser === null ? null : internalOwnResult ? internalOwnResult.leaderboard[0] ?? null : undefined;
  const nationalAvailable = !(nationalTop && nationalTop.length === 0);
  const internalAvailable = !!challenge && !(internalTop && internalTop.length === 0);
  if (!nationalAvailable && !internalAvailable) return /*#__PURE__*/React.createElement(React.Fragment, null);

  // Only one ranking has data: show it directly, without a segmented control to switch to an
  // empty one.
  const activeTab = nationalAvailable && internalAvailable ? tab : nationalAvailable ? 'national' : 'internal';
  const isNational = activeTab === 'national';
  const challengeType = event.challengeTemplate?.type || 'bttf';
  const withoutPointsUnit = event.challengeTemplate?.progressWeightingMode === 'EMPLOYEE_RATIO';
  const Button = _Button || _Button2;
  return /*#__PURE__*/React.createElement(_Box, {
    bgcolor: "#fff",
    border: "1px solid #D9E7FF",
    borderRadius: 4,
    display: "flex",
    flexDirection: "column",
    gap: 3,
    padding: 3
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    sx: {
      fontSize: 16
    }
  }, t('commons.challenges.leaderboard.title')), nationalAvailable && internalAvailable && /*#__PURE__*/React.createElement(_ToggleButtonGroup, {
    exclusive: true,
    fullWidth: true,
    onChange: (_, newTab) => {
      if (newTab) setTab(newTab);
    },
    sx: {
      bgcolor: 'rgba(221, 224, 231, 0.5)',
      borderRadius: 8,
      p: 0.5,
      '& .MuiToggleButton-root': {
        border: 'none',
        borderRadius: '999px !important',
        color: '#666a6f',
        fontSize: 14,
        fontWeight: 600,
        py: 1,
        textTransform: 'none',
        '&.Mui-selected': {
          bgcolor: '#fff',
          boxShadow: '0px 3px 4px rgba(0,0,0,0.12), 0px 3px 0.5px rgba(0,0,0,0.04)',
          color: 'text.primary',
          '&:hover': {
            bgcolor: '#fff'
          }
        }
      }
    },
    value: activeTab
  }, /*#__PURE__*/React.createElement(_ToggleButton, {
    value: "national"
  }, "\uD83C\uDF0D ", t('commons.challenges.leaderboard.national_tab')), /*#__PURE__*/React.createElement(_ToggleButton, {
    value: "internal"
  }, "\uD83C\uDFE2 ", t('commons.challenges.leaderboard.internal_tab'))), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    gap: 2
  }, isNational ? (nationalTop || [undefined, undefined, undefined]).map((item, index) => /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 1,
    key: item?.id ?? index
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    width: 24
  }, item || !nationalTop ? /*#__PURE__*/React.createElement("img", {
    src: medals[index].icon,
    width: 24
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 24,
    variant: "circular",
    width: 24
  })), /*#__PURE__*/React.createElement(GeogroupAvatar, {
    geogroup: item ? {
      title: item.parentTitle || item.title,
      photo: item.parentPhoto || item.photo
    } : undefined,
    size: 48
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "body2"
  }, item ? item.title : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 120
  })), /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    variant: "body2"
  }, item ? getProgressValueWithUnit(challengeType, item.progressValue, {
    withoutPointsUnit
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 60
  }))))) : (internalTop || [undefined, undefined, undefined]).map((item, index) => /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 1,
    key: item?.id ?? index
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    flexShrink: 0,
    justifyContent: "center",
    width: 24
  }, item || !internalTop ? /*#__PURE__*/React.createElement("img", {
    src: medals[index].icon,
    width: 24
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    height: 24,
    variant: "circular",
    width: 24
  })), /*#__PURE__*/React.createElement(UserAvatar, {
    size: 48,
    user: item && {
      username: item.username,
      profilePicture: item.profilePicture
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    minWidth: 0
  }, /*#__PURE__*/React.createElement(_Typography, {
    noWrap: true,
    fontWeight: 700,
    variant: "body2"
  }, item ? item.username || t('commons.news.deleted_user') : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 120
  })), /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    variant: "body2"
  }, item ? getProgressValueWithUnit(challengeType, item.progressValue, {
    withoutPointsUnit
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 60
  })))))), isNational ? (nationalOwnItem === undefined || nationalOwnItem) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7',
      borderStyle: 'dashed'
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#FFF5D0",
    borderRadius: 2,
    display: "flex",
    gap: 1,
    padding: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    textAlign: "center",
    variant: "body2",
    width: 24
  }, nationalOwnItem ? nationalOwnItem.rank : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 24
  })), /*#__PURE__*/React.createElement(GeogroupAvatar, {
    geogroup: nationalOwnItem ? {
      title: nationalOwnItem.parentTitle || nationalOwnItem.title,
      photo: nationalOwnItem.parentPhoto || nationalOwnItem.photo
    } : ownGeogroup,
    size: 48
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column"
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 0.5
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    variant: "body2"
  }, nationalOwnItem ? nationalOwnItem.title : ownGeogroup?.title || /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 80
  })), /*#__PURE__*/React.createElement(_Chip, {
    label: t('commons.challenges.leaderboard.national_own_label'),
    size: "small",
    sx: {
      bgcolor: 'transparent',
      color: '#326ac2',
      fontSize: 12,
      fontWeight: 800,
      height: 'auto',
      '.MuiChip-label': {
        padding: 0
      }
    }
  })), /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    variant: "body2"
  }, nationalOwnItem ? getProgressValueWithUnit(challengeType, nationalOwnItem.progressValue, {
    withoutPointsUnit
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 60
  }))))) : (internalOwnItem === undefined || internalOwnItem) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(_Divider, {
    sx: {
      borderColor: '#dde0e7',
      borderStyle: 'dashed'
    }
  }), /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    bgcolor: "#FFF5D0",
    borderRadius: 2,
    display: "flex",
    gap: 1,
    padding: 2
  }, /*#__PURE__*/React.createElement(_Typography, {
    fontWeight: 700,
    textAlign: "center",
    variant: "body2",
    width: 24
  }, internalOwnItem ? Math.max(internalOwnItem.rank, internalOwnItem.leaderboardIndex || 0) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 24
  })), /*#__PURE__*/React.createElement(UserAvatar, {
    size: 48,
    user: internalOwnItem ? {
      username: internalOwnItem.username,
      profilePicture: internalOwnItem.profilePicture
    } : currentUser
  }), /*#__PURE__*/React.createElement(_Box, {
    display: "flex",
    flexDirection: "column",
    minWidth: 0
  }, /*#__PURE__*/React.createElement(_Box, {
    alignItems: "center",
    display: "flex",
    gap: 0.5
  }, /*#__PURE__*/React.createElement(_Typography, {
    noWrap: true,
    fontWeight: 700,
    variant: "body2"
  }, internalOwnItem ? internalOwnItem.username || currentUser?.username : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 80
  })), /*#__PURE__*/React.createElement(_Chip, {
    label: t('commons.challenges.leaderboard.internal_own_label'),
    size: "small",
    sx: {
      bgcolor: 'transparent',
      color: '#326ac2',
      fontSize: 12,
      fontWeight: 800,
      height: 'auto',
      '.MuiChip-label': {
        padding: 0
      }
    }
  })), /*#__PURE__*/React.createElement(_Typography, {
    color: "text.secondary",
    variant: "body2"
  }, internalOwnItem ? getProgressValueWithUnit(challengeType, internalOwnItem.progressValue, {
    withoutPointsUnit
  }) : /*#__PURE__*/React.createElement(_Skeleton, {
    variant: "text",
    width: 60
  }))))), /*#__PURE__*/React.createElement(Button, {
    disableElevation: true,
    color: "primary",
    endIcon: /*#__PURE__*/React.createElement(_ChevronRight, null),
    onClick: () => {
      selectTab('leaderboard');
      selectLeaderboardScope(isNational ? 'national' : 'internal');
      if (isNational && nationalOwnItem) {
        onFocusOwnCompanyInLeaderboard?.(nationalOwnItem.leaderboardIndex);
      }
    },
    size: "medium",
    variant: "contained"
  }, t(isNational ? 'commons.challenges.leaderboard.national_action' : 'commons.challenges.leaderboard.internal_action')));
}
export default RankingCard;